import { Rocket, Briefcase, Lightbulb, Globe, RefreshCw } from "lucide-react";
import Markdown from "react-markdown";
import CardImage from "../tool_components/CardImage";
import ConnectionCardPdf from "./ConnectionCardPdf";
import PdfDownloadButton from "../pdf/PdfDownloadButton";
import useImagePdf from "../pdf/useImagePdf";
import { Button, Text } from "@core/index";
import { useAppSelector } from "@packages/store/hooks";
import { useState } from "react";
import PdfCustomizationModal from "@features/common/components/pdf-customization-modal";
import { PdfCustomizationSettings } from "@features/common/components/pdf-customization-modal";
import EditableMarkdown from "../tool_components/EditableMarkdown";
import { useQueryClient } from "@tanstack/react-query";
import {
  getGetRealWorldConnectionQueryKey,
  getGetUserRealWorldConnectionsQueryKey,
  updateRealWorldConnection,
} from "@packages/api/madlenAPI";
import { tC } from "../../utils/TranslateWithContentLanguage";

export interface Connection {
  title: string;
  professions: string[];
  context_description: string;
  real_world_application?: string;
}

export interface ConnectionCardProps {
  id: string;
  courseName: string;
  prompt: string;
  gradeLevel: number;
  connections: Connection[];
  imageUrl?: string;
  videoUrl?: string;
  isImageLoading: boolean;
  createdAt: string;
  onCreateAgain?: () => void;
  author: string;
  customizationSettings: PdfCustomizationSettings;
  isEditable?: boolean;
  contentLanguage: string;
}

export const ConnectionCard = ({
  id,
  courseName,
  prompt,
  gradeLevel,
  connections,
  imageUrl,
  videoUrl,
  isImageLoading,
  createdAt,
  author,
  onCreateAgain,
  isEditable = false,
  contentLanguage,
}: Omit<ConnectionCardProps, "customizationSettings">) => {
  const finalImageUrl = useImagePdf(imageUrl);
  const [showPdfCustomizationModal, setShowPdfCustomizationModal] =
    useState(false);
  const customizationSettings = useAppSelector(
    (state) => state.user.teacher?.preferences?.pdfOptions,
  );
  const queryClient = useQueryClient();

  return (
    <div>
      <div className="mb-7">
        <CardImage
          createdAt={createdAt}
          courseName={courseName}
          gradeLevel={Number(gradeLevel)}
          imageUrl={imageUrl}
          videoUrl={videoUrl}
          isLoading={isImageLoading}
          theme="orange"
          topic={
            <div className="flex items-center gap-4 justify-center">
              <div className="p-3 rounded-xl bg-orange-100 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400">
                <Rocket size={24} />
              </div>
              <div>
                <Text variant={"h1"} className="mb-2">
                  {prompt}
                </Text>
              </div>
            </div>
          }
          contentLanguage={contentLanguage}
        />
        <div className="flex justify-center md:justify-end gap-2 mt-2">
          {onCreateAgain && (
            <Button
              onClick={onCreateAgain}
              variant="solid"
              size="md"
              textColor="text-white dark:text-white"
              bgColor="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
              rounded="xl"
            >
              <RefreshCw size={18} />
              <span>{tC(contentLanguage, "common.createAnother")}</span>
            </Button>
          )}
          <PdfDownloadButton
            pdfTitle={prompt}
            openCustomizationModal={() => setShowPdfCustomizationModal(true)}
            document={
              <ConnectionCardPdf
                createdAt={createdAt}
                author={author}
                connections={connections}
                courseName={courseName}
                gradeLevel={gradeLevel}
                prompt={prompt}
                imageUrl={finalImageUrl}
                isImageLoading={false}
                customizationSettings={customizationSettings}
              />
            }
            btnTheme="bg-gradient-to-r from-orange-500 to-orange-600"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6">
        {connections?.map((conn, index) => (
          <div
            key={index}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 overflow-hidden"
          >
            <div className="p-6">
              <div className="flex items-center gap-4 mb-6">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                  <EditableMarkdown
                    value={conn.title}
                    isEditable={isEditable}
                    handleSave={async (newValue) => {
                      if (!connections) return;

                      const updatedConnections = [...connections];
                      updatedConnections[index] = {
                        ...updatedConnections[index],
                        title: newValue,
                      };

                      await updateRealWorldConnection(id, {
                        connections: updatedConnections,
                      });

                      await queryClient.invalidateQueries({
                        queryKey: getGetUserRealWorldConnectionsQueryKey(),
                      });
                      await queryClient.invalidateQueries({
                        queryKey: getGetRealWorldConnectionQueryKey(id),
                      });
                    }}
                  />
                </h2>
              </div>

              <div className="grid md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  {/* Context Description */}
                  <div>
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-xl bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400">
                        <Lightbulb size={20} />
                      </div>
                      <h3 className="font-semibold text-blue-600 dark:text-blue-400">
                        {tC(contentLanguage, "connections.context")}
                      </h3>
                    </div>
                    <div className="leading-relaxed">
                      <EditableMarkdown
                        value={conn.context_description}
                        isEditable={isEditable}
                        handleSave={async (newValue) => {
                          if (!connections) return;

                          const updatedConnections = [...connections];
                          updatedConnections[index] = {
                            ...updatedConnections[index],
                            context_description: newValue,
                          };

                          await updateRealWorldConnection(id, {
                            connections: updatedConnections,
                          });

                          await queryClient.invalidateQueries({
                            queryKey: getGetUserRealWorldConnectionsQueryKey(),
                          });
                          await queryClient.invalidateQueries({
                            queryKey: getGetRealWorldConnectionQueryKey(id),
                          });
                        }}
                      />
                    </div>
                  </div>

                  {/* Real World Application */}
                  {conn.real_world_application && (
                    <div>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 rounded-xl bg-emerald-100 dark:bg-emerald-900/50 text-emerald-600 dark:text-emerald-400">
                          <Globe size={20} />
                        </div>
                        <h3 className="font-semibold text-emerald-600 dark:text-emerald-400">
                          {tC(
                            contentLanguage,
                            "connections.connectionsApplication",
                          )}
                        </h3>
                      </div>
                      <div className="leading-relaxed">
                        <EditableMarkdown
                          value={conn.real_world_application}
                          isEditable={isEditable}
                          handleSave={async (newValue) => {
                            if (!connections) return;

                            const updatedConnections = [...connections];
                            updatedConnections[index] = {
                              ...updatedConnections[index],
                              real_world_application: newValue,
                            };

                            await updateRealWorldConnection(id, {
                              connections: updatedConnections,
                            });

                            await queryClient.invalidateQueries({
                              queryKey:
                                getGetUserRealWorldConnectionsQueryKey(),
                            });
                            await queryClient.invalidateQueries({
                              queryKey: getGetRealWorldConnectionQueryKey(id),
                            });
                          }}
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Professions */}
                <div>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="p-2 rounded-xl bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400">
                      <Briefcase size={20} />
                    </div>
                    <h3 className="font-semibold text-purple-600 dark:text-purple-400">
                      {tC(contentLanguage, "connections.professions")}
                    </h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {conn.professions.map((profession, idx) => (
                      <Text
                        key={idx}
                        variant={"span"}
                        size="sm"
                        className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700"
                      >
                        {/* Note: We're not making profession items editable as they're displayed 
                            as tags. You would need a different approach for editing array items. */}
                        <Markdown>{profession}</Markdown>
                      </Text>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* PDF Customization Modal */}
      <PdfCustomizationModal
        show={showPdfCustomizationModal}
        onClose={() => setShowPdfCustomizationModal(false)}
        hideSchoolLogoOptions
        documentComponent={
          <ConnectionCardPdf
            connections={connections}
            courseName={courseName}
            gradeLevel={gradeLevel}
            createdAt={createdAt}
            author={author}
            prompt={prompt}
            imageUrl={finalImageUrl}
            isImageLoading={false}
            customizationSettings={customizationSettings}
          />
        }
      />
    </div>
  );
};
